// ../../react-shim.js
import React from "react";

// src/popper.modifiers.ts
var matchWidth = {
  name: "matchWidth",
  enabled: true,
  phase: "beforeWrite",
  requires: ["computeStyles"],
  fn: ({ state }) => {
    state.styles.popper.width = `${state.rects.reference.width}px`;
  },
  effect: ({ state }) => {
    const reference = state.elements.reference;
    state.elements.popper.style.width = `${reference.offsetHeight}px`;
    return () => {
    };
  }
};

// src/popper.utils.ts
var oppositeDirections = {
  top: "bottom",
  bottom: "top",
  right: "left",
  left: "right"
};
var getOppositePosition = (position) => oppositeDirections[position];
var splitPlacement = (placement) => placement.split("-");
var getBoxShadow = (placement, color) => {
  if (placement.includes("top")) {
    return `2px 2px 2px 0 ${color}`;
  }
  if (placement.includes("bottom")) {
    return `-1px -1px 1px 0 ${color}`;
  }
  if (placement.includes("right")) {
    return `-1px 1px 1px 0 ${color}`;
  }
  if (placement.includes("left")) {
    return `1px -1px 1px 0 ${color}`;
  }
};
var getArrowStyles = (placement, arrowSize, arrowShadowColor) => {
  if (typeof placement !== "string")
    return {};
  const [position] = splitPlacement(placement);
  const oppositePosition = getOppositePosition(position);
  if (!oppositePosition)
    return {};
  return {
    [oppositePosition]: `-${arrowSize / 2}px`,
    width: arrowSize,
    height: arrowSize,
    position: "absolute",
    transform: "rotate(45deg)",
    boxShadow: arrowShadowColor ? getBoxShadow(placement, arrowShadowColor) : void 0
  };
};

// src/use-popper.ts
import React2 from "react";
import { createPopper } from "@popperjs/core";
var isBrowser = typeof window !== "undefined";
var useSafeLayoutEffect = isBrowser ? React2.useLayoutEffect : React2.useEffect;
var usePopper = (props) => {
  const {
    placement: initialPlacement = "bottom",
    offset: offsetProp,
    preventOverflow = true,
    fixed = false,
    forceUpdate = true,
    flip = true,
    arrowSize = 10,
    arrowShadowColor,
    gutter = arrowSize,
    eventsEnabled = true,
    modifiers
  } = props;
  const popper = React2.useRef(null);
  const referenceRef = React2.useRef(null);
  const popoverRef = React2.useRef(null);
  const arrowRef = React2.useRef(null);
  const [originalPlacement, place] = React2.useState(initialPlacement);
  const [placement, setPlacement] = React2.useState(initialPlacement);
  const [offset] = React2.useState(offsetProp || [0, gutter]);
  const [popoverStyles, setPopoverStyles] = React2.useState(
    {}
  );
  const [arrowStyles, setArrowStyles] = React2.useState({});
  const update = React2.useCallback(() => {
    if (popper.current) {
      popper.current.forceUpdate();
      return true;
    }
    return false;
  }, []);
  const modifiersOverride = modifiers ?? [];
  useSafeLayoutEffect(() => {
    if (referenceRef.current && popoverRef.current) {
      popper.current = createPopper(referenceRef.current, popoverRef.current, {
        placement: originalPlacement,
        strategy: fixed ? "fixed" : "absolute",
        modifiers: [
          {
            name: "eventListeners",
            enabled: eventsEnabled
          },
          {
            name: "applyStyles",
            enabled: false
          },
          {
            name: "flip",
            enabled: flip,
            options: { padding: 8 }
          },
          {
            name: "computeStyles",
            options: {
              gpuAcceleration: false,
              adaptive: false
            }
          },
          {
            name: "offset",
            options: { offset }
          },
          {
            name: "preventOverflow",
            enabled: preventOverflow,
            options: {
              tetherOffset: () => {
                var _a;
                return ((_a = arrowRef.current) == null ? void 0 : _a.clientHeight) || 0;
              }
            }
          },
          {
            name: "arrow",
            enabled: Boolean(arrowRef.current),
            options: { element: arrowRef.current }
          },
          {
            name: "updateState",
            phase: "write",
            enabled: true,
            fn: ({ state }) => {
              setPlacement(state.placement);
              setPopoverStyles(state.styles.popper);
              setArrowStyles(state.styles.arrow);
            }
          },
          ...modifiersOverride
        ]
      });
    }
    return () => {
      if (popper.current) {
        popper.current.destroy();
        popper.current = null;
      }
    };
  }, [
    originalPlacement,
    fixed,
    forceUpdate,
    flip,
    offset,
    preventOverflow,
    eventsEnabled
  ]);
  useSafeLayoutEffect(() => {
    const id = requestAnimationFrame(() => {
      var _a;
      if (forceUpdate) {
        (_a = popper.current) == null ? void 0 : _a.forceUpdate();
      }
    });
    return () => {
      cancelAnimationFrame(id);
    };
  }, [forceUpdate]);
  const computedArrowStyles = {
    ...arrowStyles,
    ...getArrowStyles(placement, arrowSize, arrowShadowColor)
  };
  return {
    popperInstance: popper.current,
    reference: {
      ref: referenceRef
    },
    popper: {
      ref: popoverRef,
      style: popoverStyles
    },
    arrow: {
      ref: arrowRef,
      style: computedArrowStyles
    },
    update,
    placement,
    place
  };
};
export {
  getArrowStyles,
  getBoxShadow,
  getOppositePosition,
  matchWidth,
  usePopper
};
